<template>
  <div>
    <div class="form-label mb-1" data-testid="range-title">
      <InfoTooltip :text="$t('forwards.picker.period.cover_note')">
        {{ $t("forwards.picker.period.title") }}
      </InfoTooltip>
    </div>
    <div class="row g-2 align-items-end" data-testid="range-row">
      <div class="col-auto">
        <label class="form-label fs-10 mb-1" :for="fromId">
          {{ $t("forwards.picker.period.from") }}
        </label>
        <select
          :id="fromId"
          v-model="from"
          class="form-select form-select-sm range-select"
          data-testid="range-from"
        >
          <option v-for="label in options" :key="label" :value="label">
            {{ label }}
          </option>
        </select>
      </div>

      <div class="col-auto">
        <label class="form-label fs-10 mb-1" :for="untilId">
          {{ $t("forwards.picker.period.until") }}
        </label>
        <select
          :id="untilId"
          v-model="until"
          class="form-select form-select-sm range-select"
          data-testid="range-until"
        >
          <option v-for="label in options" :key="label" :value="label">
            {{ label }}
          </option>
        </select>
      </div>
    </div>

    <p
      v-if="periods.length === 0"
      class="error-note mt-1 mb-0"
      role="alert"
      data-testid="range-invalid"
    >
      {{ $t("forwards.picker.period.invalid_range") }}
    </p>
    <p v-else class="text-body-tertiary fs-10 mt-1 mb-0">
      {{
        $t("forwards.picker.period.selected", {
          count: periods.length,
          periods: periods.join(", "),
        })
      }}
    </p>
  </div>
</template>

<script setup lang="ts">
import InfoTooltip from "@/components/common/InfoTooltip.vue";
import {
  periodOptions,
  periodRange,
  type PickerPeriodForm,
} from "@/forwards2/picker/pickerForm";
import { computed, useId } from "vue";

/**
 * The production quarters the deal sells — the first block of the picker's filter area, shared with
 * Soldera Inventory's forward tab.
 *
 * A first and a last quarter and nothing else: no width is picked (user, 2026-10-20). Each mandate
 * covers the range at its own width — a half-yearly mandate by half-year, a yearly one by year — and a
 * mandate that cannot cover all of it is left out, so a picked width would only hide mandates by accident.
 * Month vintages are made on the deal page by narrowing a monthly mandate's quarter slices.
 */
const form = defineModel<PickerPeriodForm>({ required: true });

const fromId = useId();
const untilId = useId();

const options = computed(() => periodOptions());
const periods = computed(() => periodRange(form.value));

const from = computed<string>({
  get: () => form.value.from,
  set: (value) => {
    form.value = { ...form.value, from: value };
  },
});

const until = computed<string>({
  get: () => form.value.until,
  set: (value) => {
    form.value = { ...form.value, until: value };
  },
});
</script>

<style scoped lang="scss">
.range-select {
  width: 9rem;
}
</style>
